
import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Strategy } from "@/types/strategy-types";
import { buildStrategiesQuery } from "./marketplace/buildStrategiesQuery";
import { useUserResponsesLoader } from "./marketplace/useUserResponsesLoader";
import { MarketplaceFilters } from "./marketplace/types";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { getCampaignCookie } from "./referrals/referralUtils";

export const useMarketplace = () => {
  const { toast } = useToast();
  const location = useLocation();
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [topPerformers, setTopPerformers] = useState(false); // Changed default to false
  const [selectedStrategy, setSelectedStrategy] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState("performance");
  
  // Check for the campaign cookie
  const campaignCookie = getCampaignCookie();
  const isPhemexCampaign = campaignCookie === "phemex";
  const isOkxCampaign = campaignCookie === "okx";
  
  // Initialize filters, forcing Phemex or OKX exchange based on campaign
  const [filters, setFilters] = useState<MarketplaceFilters>({
    minInvestment: "all",
    riskLevel: [],
    exchange: isPhemexCampaign ? ["Phemex"] : isOkxCampaign ? ["OKX"] : [],
    strategyTypes: [],
  });

  const { loadUserResponses } = useUserResponsesLoader(setFilters);
  const strategiesCache = useRef<Map<string, { data: Strategy[]; timestamp: number }>>(new Map());
  const CACHE_TTL = 2 * 60 * 1000; // 2 minutes cache

  // Wrap setFilters to enforce campaign-specific exchange filter
  const setFiltersWithCampaign = useCallback((newFilters: MarketplaceFilters | ((prev: MarketplaceFilters) => MarketplaceFilters)) => {
    if (isPhemexCampaign) {
      if (typeof newFilters === 'function') {
        setFilters(prev => {
          const updatedFilters = newFilters(prev);
          return {
            ...updatedFilters,
            exchange: updatedFilters.exchange.includes("Phemex") 
              ? updatedFilters.exchange 
              : [...updatedFilters.exchange, "Phemex"]
          };
        });
      } else {
        setFilters({
          ...newFilters,
          exchange: newFilters.exchange.includes("Phemex") 
            ? newFilters.exchange 
            : [...newFilters.exchange, "Phemex"]
        });
      }
    } else if (isOkxCampaign) {
      if (typeof newFilters === 'function') {
        setFilters(prev => {
          const updatedFilters = newFilters(prev);
          return {
            ...updatedFilters,
            exchange: updatedFilters.exchange.includes("OKX") 
              ? updatedFilters.exchange 
              : [...updatedFilters.exchange, "OKX"]
          };
        });
      } else {
        setFilters({
          ...newFilters,
          exchange: newFilters.exchange.includes("OKX") 
            ? newFilters.exchange 
            : [...newFilters.exchange, "OKX"]
        });
      }
    } else {
      setFilters(newFilters);
    }
  }, [isPhemexCampaign, isOkxCampaign, setFilters]);

  const activeFiltersCount = 
    (filters.minInvestment !== "all" ? 1 : 0) +
    filters.riskLevel.length +
    filters.exchange.length +
    filters.strategyTypes.length +
    (topPerformers ? 1 : 0);

  const getCacheKey = useCallback(() => {
    return JSON.stringify({
      filters,
      sortBy,
      topPerformers
    });
  }, [filters, sortBy, topPerformers]);

  const fetchStrategies = useCallback(async () => {
    setLoading(true);
    try {
      const cacheKey = getCacheKey();
      const now = Date.now();
      const cachedItem = strategiesCache.current.get(cacheKey);
      
      if (cachedItem && (now - cachedItem.timestamp < CACHE_TTL)) {
        console.log("Using cached strategies data");
        setStrategies(cachedItem.data);
        setLoading(false);
        return;
      }

      console.log("Fetching fresh strategies data");
      const { data, error } = await buildStrategiesQuery(filters, sortBy, topPerformers);

      if (error) {
        console.error("Error fetching strategies:", error);
        return;
      }

      console.log("Fetched strategies:", {
        filters,
        sortBy,
        count: data?.length,
        strategies: data,
      });

      if (data) {
        strategiesCache.current.set(cacheKey, {
          data: data,
          timestamp: now
        });
        
        setStrategies(data);
      }
    } catch (err) {
      console.error("Error in fetchStrategies:", err);
    } finally {
      setLoading(false);
    }
  }, [filters, topPerformers, sortBy, getCacheKey]);

  useEffect(() => {
    if (location.state?.filters) {
      const { minInvestment, riskLevel, exchange } = location.state.filters;

      console.log("Applying filters from location state:", {
        minInvestment,
        riskLevel,
        exchange,
      });

      const formattedFilters = {
        minInvestment: minInvestment || "all",
        riskLevel: Array.isArray(riskLevel) ? riskLevel : [],
        exchange: Array.isArray(exchange) ? exchange : [],
      };

      // Ensure Phemex/OKX is included for their respective campaigns
      if (isPhemexCampaign && !formattedFilters.exchange.includes("Phemex")) {
        formattedFilters.exchange.push("Phemex");
      }
      
      if (isOkxCampaign && !formattedFilters.exchange.includes("OKX")) {
        formattedFilters.exchange.push("OKX");
      }

      setFiltersWithCampaign((prev) => ({
        ...prev,
        ...formattedFilters,
      }));

      toast({
        title: "Filters Applied",
        description: "Your preferences have been transferred to the marketplace.",
      });
    }
  }, [location.state, isPhemexCampaign, isOkxCampaign]);

  useEffect(() => {
    fetchStrategies();
  }, [fetchStrategies]);

  return {
    strategies,
    loading,
    showMoreFilters,
    setShowMoreFilters,
    topPerformers,
    setTopPerformers,
    selectedStrategy,
    setSelectedStrategy,
    filters,
    setFilters: setFiltersWithCampaign,
    sortBy,
    setSortBy,
    activeFiltersCount,
    isPhemexCampaign,
    isOkxCampaign,
  };
};
